'use client';

import { useMemo, useState } from 'react';
import {
  CaretDownIcon,
  DotsThreeVerticalIcon,
  EyeSlashIcon,
  FlagIcon,
  FunnelIcon,
  ListMagnifyingGlassIcon,
  MagnifyingGlassIcon,
  PencilSimpleIcon,
  PlusIcon,
  TargetIcon,
  WalletIcon,
} from '@phosphor-icons/react';
import type { CategoryBudgetStatus, CategoryChildLimit, CategoryOverviewRow } from '@/types';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { cn, normalizeForSearch } from '@/lib/utils';
import { track } from '@/lib/telemetry';
import { categoryPathLabel, type CategoryNode, type CategoryTree } from '@/lib/categories/tree';
import { CategoryIcon } from '@/components/CategoryIcon';
import { InlineLimitEditor } from '@/components/categories/InlineLimitEditor';
import { MoneyValue } from '@/components/ui/MoneyValue';
import { getCategoryMeta, type CategoryIndex } from '@/components/transactions/screen/categoryIndex';
import { CategoryActionsMenu, type CategoryRowActions } from './CategoryActionsMenu';
import { categoryVisual, type CategoryVisual } from '@/components/categories/categoryHelpers';
import { STATUS_META, UNCATEGORIZED_CATEGORY_ID } from './helpers';

/**
 * The directory's period words. The personal page is monthly; a caller whose budget is not monthly passes its own
 * wording.
 */
export interface CategoryRowsCopy {
  usageHeader: string;
  limitHeader: string;
  expenseBand: string;
  limitsTotalSuffix: string;
  /** Under an open root editor and as the kebab item's sublabel. */
  limitHint: string;
  /** Next to an open child editor without a limit yet. */
  childLimitHint: string;
  empty: string;
}

// eslint-disable-next-line react-refresh/only-export-components
export const PERSONAL_CATEGORY_COPY: CategoryRowsCopy = {
  usageHeader: 'Uso no mês',
  limitHeader: 'Limite mensal',
  expenseBand: 'O que sai da sua conta e conta no orçamento do mês.',
  limitsTotalSuffix: 'em limites no mês',
  limitHint: 'Limite mensal da categoria',
  childLimitHint: 'limite mensal',
  empty: 'Nenhuma categoria para mostrar ainda — conecte um banco ou crie uma categoria.',
};

interface CategoryRowsProps extends Omit<CategoryRowActions, 'onEditLimit'> {
  rows: CategoryOverviewRow[];
  /** Child (non-root) categories carrying their own limits, keyed under rootRef. */
  childLimits: CategoryChildLimit[];
  /** Labels/colors for child lines that fall outside the root's direct children. */
  categoryIndex: CategoryIndex;
  /** The full taxonomy — the page lists every category, so children come from here. */
  categoryTree: CategoryTree;
  /** Categories that have merchant rules — gates the kebab's "Regras" item. */
  ruleRefs: ReadonlySet<string>;
  savingRef: string | null;
  /**
   * A limit save is in flight and every OTHER limit must wait for it: a space saves its whole budget per edit, so a
   * second edit built before the first came back would PUT a stale budget and undo it. Editors stay closed/disabled.
   */
  limitsBusy?: boolean;
  /** No limit can be set or changed (a space's viewer, a closed space, a partner preview); everything else reads as usual. */
  readOnly?: boolean;
  /** What the usage line says after the amount; the personal page is monthly, a trip's space is not. */
  usageSuffix?: string;
  /** The period words of headers, bands, hints and the empty state; defaults to the personal (monthly) copy. */
  copy?: CategoryRowsCopy;
  /** Rows that can never take a limit (a space's "Sem categoria compartilhada" bucket): the limit cell stays quiet. */
  lockedRefs?: ReadonlySet<string>;
  /** Expense rows without a limit still show what was spent (a space has no other place for that number). */
  showSpendWithoutLimit?: boolean;
  /** The note under the list; the personal one talks about archived categories, which a space has none of. */
  footnote?: string;
  onSaveLimit: (categoryRef: string, limitAmount: number | null) => void;
  /** Income rows: open the meta picker (link/alter/remove). */
  onLinkGoal: (row: CategoryOverviewRow) => void;
  onReviewChipClick: () => void;
  /** Child-line actions (leaf grain). */
  onViewChildTransactions: (rootRef: string, leafId: string) => void;
  onViewChildRules: (rootRef: string) => void;
}

const NO_LOCKED_REFS: ReadonlySet<string> = new Set();

// Desktop grid: icon · categoria · uso no mês · limite mensal · kebab. Below @2xl each
// row is the phone layout — chead (icon · nome/meta · kebab), then the usage and limit
// lines indented past the icon. At @2xl the wrappers become display:contents and every
// cell takes an explicit column AND row-start-1 so the DOM keeps the mobile-first order.
const GRID_COLS =
  '@2xl:grid @2xl:grid-cols-[3rem_minmax(0,1fr)_9.5rem_11rem_2rem] @2xl:items-center @2xl:gap-x-4';

type GroupKey = 'expense' | 'income' | 'neutral';
type KindFilter = 'all' | GroupKey;

// The selected kind chip survives reloads — focusing "Despesas" once should stick.
const KIND_FILTER_KEY = 'finance-control:categorias-kind-filter';

const KIND_CHIP_LABELS: Record<GroupKey, string> = {
  expense: 'Despesas',
  income: 'Receitas',
  neutral: 'Mov. internas',
};

function readKindFilter(): KindFilter {
  try {
    const raw = localStorage.getItem(KIND_FILTER_KEY);
    return raw === 'expense' || raw === 'income' || raw === 'neutral' ? raw : 'all';
  } catch {
    return 'all';
  }
}

/**
 * Directory order: alphabetical, with "Outros" pinned last (it's the catch-all,
 * not a peer) and archived categories after everything (dimmed history).
 */
function sortGroupRows(rows: CategoryOverviewRow[]): CategoryOverviewRow[] {
  const rank = (r: CategoryOverviewRow) =>
    (r.isArchived ? 2 : 0) + (r.categoryRef === UNCATEGORIZED_CATEGORY_ID ? 1 : 0);
  return rows
    .slice()
    .sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }));
}

// The per-row status is stamped by the backend; the group aggregate only exists
// client-side, so it mirrors the exact rule rows are stamped with
// (CategoryBudgetBusinessRules.ComputeStatus): <80 ok, 80–100 warning, >100 exceeded.
function aggregateStatus(percent: number): CategoryBudgetStatus {
  return percent > 100 ? 'exceeded' : percent >= 80 ? 'warning' : 'on_track';
}

// The taxonomy has depth-3 branches — the directory lists every descendant, not just
// the direct children (deeper entries render their breadcrumb label).
function flattenDescendants(node: CategoryNode | undefined): CategoryNode[] {
  if (!node) return [];
  const out: CategoryNode[] = [];
  const walk = (n: CategoryNode) => {
    for (const child of n.children) {
      out.push(child);
      walk(child);
    }
  };
  walk(node);
  return out;
}

export function CategoryRows({
  rows,
  childLimits,
  categoryIndex,
  categoryTree,
  ruleRefs,
  savingRef,
  limitsBusy = false,
  readOnly = false,
  usageSuffix = 'no mês',
  copy = PERSONAL_CATEGORY_COPY,
  lockedRefs = NO_LOCKED_REFS,
  showSpendWithoutLimit = false,
  footnote = 'Categorias arquivadas permanecem no fim da lista com o histórico preservado. Para ver os lançamentos de uma categoria, use o menu de ações da linha.',
  onSaveLimit,
  onLinkGoal,
  onReviewChipClick,
  onEdit,
  onViewTransactions,
  onViewRules,
  onToggleArchived,
  onViewChildTransactions,
  onViewChildRules,
}: CategoryRowsProps) {
  const formatCurrency = useFormatCurrency();
  const [search, setSearch] = useState('');
  // Single-open drill-down: expanding one row collapses the others.
  const [expandedRef, setExpandedRef] = useState<string | null>(null);
  const [kindFilter, setKindFilter] = useState<KindFilter>(readKindFilter);

  // Case- AND accent-insensitive ("saude" finds "Saúde") — the same normalization the
  // transactions category search uses.
  const needle = normalizeForSearch(search.trim());
  const visible = useMemo(() => {
    if (!needle) return rows;
    // The directory lists whole subtrees, so the search must too: a root stays
    // visible when its own name OR any descendant label matches (the row then
    // auto-expands to the matching children — see CategoryRow).
    return rows.filter(
      (r) =>
        normalizeForSearch(r.name).includes(needle) ||
        flattenDescendants(categoryTree.byId.get(r.categoryRef)).some((n) =>
          normalizeForSearch(n.label).includes(needle),
        ),
    );
  }, [rows, needle, categoryTree]);

  // An active search overrides the kind filter — a match must never hide inside a
  // filtered-out group.
  const searching = needle !== '';
  const selectKindFilter = (next: KindFilter) => {
    // Re-clicking the active chip is a no-op — don't inflate filter usage.
    if (next !== kindFilter) {
      track('ui.filter_applied', {
        surface: 'orcamento',
        filter: 'kind',
        action: next === 'all' ? 'clear' : 'set',
        method: 'chip',
        ...(next === 'all' ? {} : { value: next }),
      });
    }
    setKindFilter(next);
    try {
      localStorage.setItem(KIND_FILTER_KEY, next);
    } catch {
      // Private mode / quota — the filter still applies for this session.
    }
  };

  const expenseRows = sortGroupRows(visible.filter((r) => r.kind === 'expense'));
  const incomeRows = sortGroupRows(visible.filter((r) => r.kind === 'income'));
  const neutralRows = sortGroupRows(visible.filter((r) => r.kind === 'neutral'));

  // Chip counts and band aggregates come from the FULL row set, not the searched one;
  // a persisted filter whose group has no rows falls back to "Todas".
  const groupCounts: Record<GroupKey, number> = {
    expense: rows.filter((r) => r.kind === 'expense').length,
    income: rows.filter((r) => r.kind === 'income').length,
    neutral: rows.filter((r) => r.kind === 'neutral').length,
  };
  const expenseAgg = useMemo(() => {
    // Top-most wins (mirrors CategoryOverviewService): a child limit under a LIMITED
    // root is a refinement of budget the root already counts — only children of
    // unlimited roots add to the aggregate. Archived roots stay out of the sums.
    const limited = rows.filter((r) => r.kind === 'expense' && r.limit != null && !r.isArchived);
    const limitedRootRefs = new Set(
      rows.filter((r) => r.limit != null).map((r) => r.categoryRef),
    );
    // An archived subtree stays out of the aggregate entirely — children of an
    // archived (even unlimited) root must not count.
    const archivedRootRefs = new Set(
      rows.filter((r) => r.isArchived).map((r) => r.categoryRef),
    );
    const countedChildren = childLimits.filter(
      (c) =>
        c.rootRef == null ||
        (!limitedRootRefs.has(c.rootRef) && !archivedRootRefs.has(c.rootRef)),
    );
    const limit =
      limited.reduce((sum, r) => sum + (r.limit ?? 0), 0) +
      countedChildren.reduce((sum, c) => sum + c.limit, 0);
    const spent =
      limited.reduce((sum, r) => sum + r.spent, 0) +
      countedChildren.reduce((sum, c) => sum + c.spent, 0);
    return {
      limitedCount: limited.length + countedChildren.length,
      limit,
      spent,
      percent: limit > 0 ? (spent / limit) * 100 : 0,
    };
  }, [rows, childLimits]);
  const incomeTotal = rows.filter((r) => r.kind === 'income').reduce((sum, r) => sum + r.spent, 0);
  const incomeGoalCount = rows.filter((r) => r.kind === 'income' && r.linkedGoal != null).length;
  const neutralTotal = rows.filter((r) => r.kind === 'neutral').reduce((sum, r) => sum + r.spent, 0);

  const activeFilter: KindFilter =
    kindFilter !== 'all' && groupCounts[kindFilter] === 0 ? 'all' : kindFilter;
  const showGroup = (key: GroupKey) =>
    searching || activeFilter === 'all' || activeFilter === key;
  const presentGroups = (Object.keys(groupCounts) as GroupKey[]).filter(
    (k) => groupCounts[k] > 0,
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Empty↔non-empty transitions only (trimmed — whitespace-only is not a
    // search) — never per keystroke, never the text.
    const had = search.trim() !== '';
    const has = e.target.value.trim() !== '';
    if (!had && has) {
      track('ui.filter_applied', { surface: 'orcamento', filter: 'search', action: 'set', method: 'search' });
    } else if (had && !has) {
      track('ui.filter_applied', { surface: 'orcamento', filter: 'search', action: 'clear', method: 'search' });
    }
    setSearch(e.target.value);
  };

  const rowProps = {
    searchNeedle: needle,
    categoryIndex,
    categoryTree,
    childLimits,
    ruleRefs,
    savingRef,
    limitsBusy,
    readOnly,
    usageSuffix,
    copy,
    showSpendWithoutLimit,
    onSaveLimit,
    onLinkGoal,
    onReviewChipClick,
    onEdit,
    onViewTransactions,
    onViewRules,
    onToggleArchived,
    onViewChildTransactions,
    onViewChildRules,
  };

  const renderGroup = (groupRows: CategoryOverviewRow[]) => (
    <div className="divide-y divide-border-subtle">
      {groupRows.map((row) => (
        <CategoryRow
          key={row.categoryRef}
          row={row}
          locked={lockedRefs.has(row.categoryRef)}
          saving={savingRef === row.categoryRef}
          expanded={expandedRef === row.categoryRef}
          onToggleExpanded={() =>
            setExpandedRef((cur) => (cur === row.categoryRef ? null : row.categoryRef))
          }
          hasRules={ruleRefs.has(row.categoryRef)}
          {...rowProps}
        />
      ))}
    </div>
  );

  return (
    // overflow-clip (NOT hidden): hidden would make the card a scroll container and kill
    // the kind filter's position:sticky; clip still crops children to the radius.
    <section data-slot="category-directory" className="overflow-clip rounded-3xl border border-border-subtle bg-surface shadow-sm">
      <div className="flex flex-wrap items-center gap-3 border-b border-border-subtle px-4 py-3 sm:px-5">
        <span className="text-2xs font-semibold tracking-widest text-text-primary uppercase">
          Categorias
        </span>
        <span className="font-mono text-2xs text-neutral-400 tabular-nums">{rows.length}</span>
        <div className="ml-auto flex items-center gap-3">
          <StatusLegend />
          <div className="hidden items-center gap-1.5 rounded-md border border-border-subtle px-2 py-1 focus-within:border-brand-600 focus-within:ring-[3px] focus-within:ring-brand-200/60 sm:flex">
            <MagnifyingGlassIcon className="size-3.5 text-text-tertiary" />
            <input
              value={search}
              onChange={handleSearchChange}
              placeholder="Buscar categoria…"
              className="w-36 bg-transparent text-xs outline-none placeholder:text-text-tertiary"
            />
          </div>
        </div>
      </div>

      {/* Kind filter — sticks just below the app header (sticky top-0 h-16) while the
          long list scrolls, so switching groups never needs vertical travel. */}
      {presentGroups.length >= 2 && (
        <div className="sticky top-16 z-30 border-b border-border-subtle bg-surface/85 px-3 py-2 backdrop-blur-xl sm:px-4">
          <div
            role="group"
            aria-label="Filtrar categorias por tipo"
            className="flex w-fit max-w-full gap-1 overflow-x-auto rounded-xl bg-muted p-1"
          >
            <KindChip
              label="Todas"
              active={activeFilter === 'all'}
              onSelect={() => selectKindFilter('all')}
            />
            {presentGroups.map((key) => (
              <KindChip
                key={key}
                label={KIND_CHIP_LABELS[key]}
                count={groupCounts[key]}
                active={activeFilter === key}
                onSelect={() => selectKindFilter(key)}
              />
            ))}
          </div>
        </div>
      )}

      {visible.length === 0 ? (
        <div className="px-4 py-8 text-center text-sm text-text-tertiary sm:px-5">
          {rows.length === 0
            ? copy.empty
            : 'Nenhuma categoria corresponde à busca.'}
        </div>
      ) : (
        <div className="@container">
          {/* Column headers — only when the grid columns exist. */}
          <div
            className={`hidden border-b border-border-subtle px-4 py-2 pl-5 font-mono text-3xs font-semibold tracking-[0.1em] text-text-tertiary uppercase ${GRID_COLS}`}
          >
            <span />
            <span>Categoria</span>
            <span>{copy.usageHeader}</span>
            <span className="text-right">{copy.limitHeader}</span>
            <span />
          </div>

          {showGroup('expense') && expenseRows.length > 0 && (
            <>
              <GroupBand
                label="Despesas"
                count={groupCounts.expense}
                extra={expenseAgg.limitedCount > 0 ? `${expenseAgg.limitedCount} com limite` : undefined}
                description={copy.expenseBand}
              >
                {expenseAgg.limit > 0 && (
                  <>
                    <BurnBar
                      percent={expenseAgg.percent}
                      statusKey={aggregateStatus(expenseAgg.percent)}
                      className="justify-end"
                    />
                    <p className="mt-0.5 font-mono text-3xs text-text-tertiary tabular-nums">
                      <MoneyValue text={formatCurrency(expenseAgg.spent)} /> de{' '}
                      <MoneyValue text={formatCurrency(expenseAgg.limit)} /> {copy.limitsTotalSuffix}
                    </p>
                  </>
                )}
              </GroupBand>
              {renderGroup(expenseRows)}
            </>
          )}

          {showGroup('income') && incomeRows.length > 0 && (
            <>
              <GroupBand
                label="Receitas"
                count={groupCounts.income}
                extra={incomeGoalCount > 0 ? `${incomeGoalCount} com meta` : undefined}
                description="O que entra — salário, rendimentos e outras entradas."
              >
                <p className="font-mono text-xs font-medium text-success-text tabular-nums">
                  +<MoneyValue text={formatCurrency(incomeTotal)} />
                </p>
                <p className="mt-0.5 font-mono text-3xs text-text-tertiary">recebidos no mês</p>
              </GroupBand>
              {renderGroup(incomeRows)}
            </>
          )}

          {showGroup('neutral') && neutralRows.length > 0 && (
            <>
              <GroupBand
                label="Movimentações internas"
                count={groupCounts.neutral}
                description="Dinheiro que circula entre suas contas — transferências, investimentos e pagamento de fatura. Não conta como gasto."
              >
                <p className="font-mono text-xs font-medium text-text-secondary tabular-nums">
                  <MoneyValue text={formatCurrency(neutralTotal)} />
                </p>
                <p className="mt-0.5 font-mono text-3xs text-text-tertiary">movimentados no mês</p>
              </GroupBand>
              {renderGroup(neutralRows)}
            </>
          )}
        </div>
      )}

      <div className="flex items-start gap-2 border-t border-border-subtle bg-surface-sunken px-4 py-3 text-2xs leading-relaxed text-text-tertiary sm:px-5">
        <EyeSlashIcon className="mt-px size-3.5 shrink-0 text-neutral-400" />
        <p>{footnote}</p>
      </div>
    </section>
  );
}

// The progress-color scale legend.
function StatusLegend() {
  const entries = [STATUS_META.on_track, STATUS_META.warning, STATUS_META.exceeded];
  return (
    <div className="flex items-center gap-2.5" aria-hidden>
      {entries.map((status) => (
        <span key={status.label} className="flex items-center gap-1.5 text-2xs text-text-secondary">
          <span className={`size-2 rounded-xs ${status.bar}`} />
          {status.label}
        </span>
      ))}
    </div>
  );
}

// One segment of the kind filter — ExpenseTabBar's white-pill-on-gray-track recipe,
// sized for thumbs (min-h-9 inside the p-1 track ≈ 44px row).
function KindChip({
  label,
  count,
  active,
  onSelect,
}: {
  label: string;
  count?: number;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onSelect}
      className={cn(
        'flex min-h-9 shrink-0 cursor-pointer items-center gap-1.5 rounded-lg px-3 text-xs font-semibold whitespace-nowrap',
        active ? 'bg-surface text-brand-700 shadow-sm' : 'text-text-secondary hover:text-text-primary',
      )}
    >
      {label}
      {count != null && (
        <span
          className={cn(
            'font-mono text-3xs font-medium tabular-nums',
            active ? 'text-text-tertiary' : 'text-neutral-400',
          )}
        >
          {count}
        </span>
      )}
    </button>
  );
}

/**
 * Group context band — the kind split is the platform's mental model, so each group
 * explains what it means in one line and carries its month aggregate on the right
 * (the in-context replacement for the removed KPI cards).
 */
function GroupBand({
  label,
  count,
  extra,
  description,
  children,
}: {
  label: string;
  count: number;
  extra?: string;
  description: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 border-b border-border-subtle bg-surface-sunken px-4 py-2.5 sm:px-5">
      <div className="min-w-0">
        <h3 className="text-sm font-semibold text-text-primary">
          {label}
          <span className="ml-2 font-mono text-3xs font-normal tracking-[0.05em] text-neutral-400 uppercase">
            {count} {count === 1 ? 'categoria' : 'categorias'}
            {extra && <> · {extra}</>}
          </span>
        </h3>
        <p className="text-2xs text-text-tertiary">{description}</p>
      </div>
      {children && <div className="ml-auto shrink-0 text-right">{children}</div>}
    </div>
  );
}

// The shared thin progress bar: status-colored fill on the muted track, mono percent.
function BurnBar({
  percent,
  statusKey,
  className,
}: {
  percent: number;
  statusKey: CategoryBudgetStatus;
  className?: string;
}) {
  const status = STATUS_META[statusKey];
  return (
    <span className={cn('flex items-center gap-2', className)}>
      <span className="h-1 w-full max-w-28 flex-1 overflow-hidden rounded-full bg-muted">
        <span
          className={`block h-full min-w-1 rounded-full ${status.bar}`}
          style={{ width: `${Math.min(100, Math.max(0, percent))}%` }}
        />
      </span>
      <span className={`font-mono text-3xs font-semibold tabular-nums ${status.text}`}>
        {Math.round(percent)}%
      </span>
    </span>
  );
}

// The transactions-page icon treatment (TransactionRow): tinted rounded square with the
// category icon in the darkened accent. Replaces the old burn ring — usage lives in the
// "Uso no mês" bar now.
function CategoryTile({
  icon,
  visual,
  size = 'md',
}: {
  icon: string | null;
  visual: CategoryVisual;
  size?: 'md' | 'sm';
}) {
  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center',
        size === 'md' ? 'size-11 rounded-2xl' : 'size-8 rounded-lg',
      )}
      style={{ backgroundColor: visual.backgroundColor }}
    >
      <CategoryIcon
        name={icon}
        className={size === 'md' ? 'size-5.5' : 'size-4'}
        style={{ color: visual.color }}
      />
    </span>
  );
}

/**
 * "Uso no mês": expense rows with a limit burn the bar; income rows with a linked meta
 * show the goal's progress (same cell, different fill semantics). Everything else shows
 * a quiet dash on desktop and nothing on phones.
 */
function UsageCell({ row, suffix, showSpendWithoutLimit }: { row: CategoryOverviewRow; suffix: string; showSpendWithoutLimit: boolean }) {
  const formatCurrency = useFormatCurrency();

  if (row.kind === 'expense' && row.limit == null && showSpendWithoutLimit && row.spent > 0) {
    return (
      <div className="ml-14 min-w-0 @2xl:col-start-3 @2xl:row-start-1 @2xl:ml-0">
        <p className="font-mono text-3xs text-text-tertiary tabular-nums">
          <MoneyValue text={formatCurrency(row.spent)} /> {suffix}
        </p>
      </div>
    );
  }

  if (row.kind === 'expense' && row.limit != null) {
    const burn = row.burnPercent ?? (row.limit > 0 ? (row.spent / row.limit) * 100 : 0);
    const overshoot = row.spent > row.limit ? row.spent - row.limit : 0;
    return (
      <div className="ml-14 min-w-0 @2xl:col-start-3 @2xl:row-start-1 @2xl:ml-0">
        <BurnBar percent={burn} statusKey={row.status} />
        <p className="mt-0.5 font-mono text-3xs text-text-tertiary tabular-nums">
          <MoneyValue text={formatCurrency(row.spent)} /> {suffix}
          {overshoot > 0 && (
            <span className="ml-1.5 font-medium text-error-text">
              <MoneyValue text={formatCurrency(overshoot)} /> acima
            </span>
          )}
        </p>
      </div>
    );
  }

  if (row.kind === 'income' && row.linkedGoal) {
    return (
      <div className="ml-14 min-w-0 @2xl:col-start-3 @2xl:row-start-1 @2xl:ml-0">
        <BurnBar percent={row.linkedGoal.progressPercent} statusKey="on_track" />
        <p className="mt-0.5 font-mono text-3xs text-text-tertiary">progresso da meta</p>
      </div>
    );
  }

  return (
    <span className="hidden text-2xs text-neutral-400 @2xl:col-start-3 @2xl:row-start-1 @2xl:block">
      —
    </span>
  );
}

function CategoryRow({
  row,
  locked,
  saving,
  searchNeedle,
  categoryIndex,
  categoryTree,
  childLimits,
  ruleRefs,
  savingRef,
  limitsBusy,
  readOnly,
  usageSuffix,
  copy,
  showSpendWithoutLimit,
  expanded,
  onToggleExpanded,
  hasRules,
  onSaveLimit,
  onLinkGoal,
  onReviewChipClick,
  onEdit,
  onViewTransactions,
  onViewRules,
  onToggleArchived,
  onViewChildTransactions,
  onViewChildRules,
}: {
  row: CategoryOverviewRow;
  locked: boolean;
  saving: boolean;
  searchNeedle: string;
  categoryIndex: CategoryIndex;
  categoryTree: CategoryTree;
  childLimits: CategoryChildLimit[];
  ruleRefs: ReadonlySet<string>;
  savingRef: string | null;
  limitsBusy: boolean;
  readOnly: boolean;
  usageSuffix: string;
  copy: CategoryRowsCopy;
  showSpendWithoutLimit: boolean;
  expanded: boolean;
  onToggleExpanded: () => void;
  hasRules: boolean;
  onSaveLimit: CategoryRowsProps['onSaveLimit'];
  onLinkGoal: CategoryRowsProps['onLinkGoal'];
  onReviewChipClick: () => void;
  onViewChildTransactions: (rootRef: string, leafId: string) => void;
  onViewChildRules: (rootRef: string) => void;
} & Omit<CategoryRowActions, 'onEditLimit'>) {
  const formatCurrency = useFormatCurrency();
  const [editing, setEditing] = useState(false);

  const childNodes = flattenDescendants(categoryTree.byId.get(row.categoryRef));
  const expandable = childNodes.length > 0;
  // A search that matched a descendant must EXPOSE it — the row opens by itself and the
  // child list filters to the matches (the row may be visible only because of them).
  const searchExpanded =
    searchNeedle !== '' &&
    childNodes.some((n) => normalizeForSearch(n.label).includes(searchNeedle));
  const isExpanded = expanded || searchExpanded;
  const isIncome = row.kind === 'income';
  const isExpense = row.kind === 'expense';
  const canEdit = !readOnly && !locked && !row.isArchived && isExpense && !limitsBusy;
  const visual = categoryVisual(row.categoryRef, row.source, row.color);
  const icon = row.icon ?? getCategoryMeta(categoryIndex, row.categoryRef).iconName;

  const openEditor = () => {
    if (!canEdit) return;
    setEditing(true);
  };
  const openGoalPicker = () => onLinkGoal(row);
  // An editor opened before the directory turned read-only (a partner preview starting, the space closing) stops
  // rendering and can no longer save; it does not come back on its own either, since it closes on the next commit.
  const editorOpen = editing && !readOnly;
  const commitLimit = (value: number | null) => {
    if (!readOnly) onSaveLimit(row.categoryRef, value);
    setEditing(false);
  };
  const cancelEditing = () => setEditing(false);

  return (
    <div data-slot="category-row">
      <div
        className={`relative flex flex-col gap-2.5 p-3 pl-4 @2xl:p-4 @2xl:pl-5 ${GRID_COLS} ${row.isArchived ? 'opacity-60' : ''}`}
      >
        {/* chead: icon · nome/meta · kebab. */}
        <div className="flex min-w-0 items-center gap-3 @2xl:contents">
          <CategoryTile icon={icon} visual={visual} />
          <div className="min-w-0 flex-1 @2xl:col-start-2 @2xl:row-start-1">
            <p className="flex items-center gap-2 truncate text-sm font-semibold text-text-primary">
              {expandable ? (
                <button
                  type="button"
                  aria-expanded={isExpanded}
                  aria-label={`Detalhar ${row.name}`}
                  onClick={onToggleExpanded}
                  className="inline-flex min-w-0 cursor-pointer items-center gap-1.5 text-left hover:text-brand-primary"
                >
                  <span className="truncate">{row.name}</span>
                  <CaretDownIcon
                    className={cn('size-3.5 shrink-0 text-text-tertiary transition-transform', isExpanded && 'rotate-180')}
                  />
                </button>
              ) : (
                row.name
              )}
            </p>
            <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-2xs text-text-tertiary tabular-nums">
              {expandable && (
                <span>
                  {childNodes.length} {childNodes.length === 1 ? 'subcategoria' : 'subcategorias'}
                </span>
              )}
              {row.source === 'custom' && (
                <span className="rounded-full bg-muted px-1.5 py-px text-3xs font-medium text-text-tertiary">
                  personalizada
                </span>
              )}
              {row.isArchived && (
                <span className="rounded-full bg-muted px-1.5 py-px text-3xs font-medium text-text-tertiary">
                  arquivada
                </span>
              )}
              {row.needsReviewCount > 0 && (
                <button
                  type="button"
                  onClick={onReviewChipClick}
                  className="inline-flex cursor-pointer items-center gap-1 rounded-full bg-warning-surface px-1.5 py-px text-3xs font-semibold text-warning-text"
                >
                  <FlagIcon className="size-2.5" /> {row.needsReviewCount} a revisar
                </button>
              )}
            </p>
          </div>
          <span className="shrink-0 @2xl:col-start-5 @2xl:row-start-1">
            <CategoryActionsMenu
              row={row}
              hasRules={hasRules}
              canEditLimit={canEdit}
              hideLimit={readOnly || locked}
              limitHint={copy.limitHint}
              onEdit={onEdit}
              onEditLimit={openEditor}
              onLinkGoal={openGoalPicker}
              onViewTransactions={onViewTransactions}
              onViewRules={onViewRules}
              onToggleArchived={onToggleArchived}
            />
          </span>
        </div>

        <UsageCell row={row} suffix={usageSuffix} showSpendWithoutLimit={showSpendWithoutLimit} />

        {/* Limit cell: quiet mono value + pencil, or the compact secondary button. */}
        <div className="ml-14 min-w-0 @2xl:col-start-4 @2xl:row-start-1 @2xl:ml-0">
          {isIncome ? (
            // Receitas carry a meta link where expenses carry a limit: a quiet goal chip
            // when linked, the button affordance otherwise.
            row.linkedGoal ? (
              <button
                type="button"
                disabled={row.isArchived}
                onClick={openGoalPicker}
                className="inline-flex max-w-full cursor-pointer items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-2xs font-medium text-text-secondary transition-colors enabled:hover:bg-accent/70 disabled:cursor-not-allowed"
              >
                <TargetIcon className="size-3 shrink-0 text-success-text" />
                <span className="truncate">{row.linkedGoal.name}</span>
                <span className="font-mono text-text-tertiary tabular-nums">
                  {Math.round(row.linkedGoal.progressPercent)}%
                </span>
              </button>
            ) : (
              <Button variant="secondary" size="sm" disabled={row.isArchived} onClick={openGoalPicker}>
                <TargetIcon className="size-3.5" /> Vincular meta
              </Button>
            )
          ) : !isExpense ? (
            <span className="text-2xs text-neutral-400">não conta como gasto</span>
          ) : editorOpen ? (
            <InlineLimitEditor
              // A category without a limit seeds the editor from its 3-month average — a
              // realistic starting number the user adjusts, instead of a blank field.
              initialValue={row.limit ?? (row.average3mo > 0 ? row.average3mo : null)}
              saving={saving || limitsBusy}
              onCommit={commitLimit}
              onCancel={cancelEditing}
            />
          ) : (
            <span className="flex flex-wrap items-center gap-2 @2xl:justify-end">
              {row.limit != null && readOnly ? (
                <span className="inline-flex items-center font-mono text-sm font-medium text-text-primary tabular-nums"><MoneyValue text={formatCurrency(row.limit)} /></span>
              ) : row.limit != null ? (
                <button
                  type="button"
                  disabled={!canEdit || saving}
                  onClick={openEditor}
                  className="group/lv inline-flex cursor-pointer items-center gap-1.5 font-mono text-sm font-medium text-text-primary tabular-nums disabled:cursor-not-allowed"
                >
                  <MoneyValue text={formatCurrency(row.limit)} />
                  <PencilSimpleIcon className="size-3.5 text-neutral-300 group-hover/lv:text-text-tertiary" />
                </button>
              ) : readOnly || locked ? (
                <span className="text-2xs text-neutral-400">sem limite</span>
              ) : (
                <Button variant="secondary" size="sm" disabled={!canEdit || saving} onClick={openEditor}>
                  <PlusIcon className="size-3.5" /> Definir limite
                </Button>
              )}
            </span>
          )}
          {editorOpen && (
            <p className="mt-1.5 text-2xs text-text-tertiary">
              {copy.limitHint}
              {row.limit != null ? ' · 0 ou vazio remove o limite' : ''}
            </p>
          )}
        </div>
      </div>
      {isExpanded && (
        <CategoryChildRows
          row={row}
          childNodes={childNodes}
          filterNeedle={searchExpanded ? searchNeedle : ''}
          categoryTree={categoryTree}
          categoryIndex={categoryIndex}
          childLimits={childLimits.filter((c) => c.rootRef === row.categoryRef)}
          ruleRefs={ruleRefs}
          savingRef={savingRef}
          limitsBusy={limitsBusy}
          readOnly={readOnly}
          childLimitHint={copy.childLimitHint}
          onSaveLimit={onSaveLimit}
          onViewChildTransactions={onViewChildTransactions}
          onViewChildRules={onViewChildRules}
        />
      )}
    </div>
  );
}

interface ChildLine {
  id: string;
  label: string;
  iconName: string | null;
  limit: CategoryChildLimit | null;
}

/**
 * The root's descendant categories as indented lines — the FULL taxonomy (the page is
 * the category directory, not a period view), all depths flattened with breadcrumb
 * labels past the direct children, merged with the entries that carry their own limit,
 * which show the burn bar + inline editor. Limits stamped on refs outside the taxonomy
 * subtree still get a line via the category index.
 */
function CategoryChildRows({
  row,
  childNodes,
  filterNeedle,
  categoryTree,
  categoryIndex,
  childLimits,
  ruleRefs,
  savingRef,
  limitsBusy,
  readOnly,
  childLimitHint,
  onSaveLimit,
  onViewChildTransactions,
  onViewChildRules,
}: {
  row: CategoryOverviewRow;
  childNodes: readonly CategoryNode[];
  /** Search-driven expansion shows only the matching lines; '' shows all. */
  filterNeedle: string;
  categoryTree: CategoryTree;
  categoryIndex: CategoryIndex;
  childLimits: CategoryChildLimit[];
  ruleRefs: ReadonlySet<string>;
  savingRef: string | null;
  limitsBusy: boolean;
  readOnly: boolean;
  childLimitHint: string;
  onSaveLimit: CategoryRowsProps['onSaveLimit'];
  onViewChildTransactions: (rootRef: string, leafId: string) => void;
  onViewChildRules: (rootRef: string) => void;
}) {
  const [editingRef, setEditingRef] = useState<string | null>(null);

  const lines = useMemo<ChildLine[]>(() => {
    const limitByRef = new Map(childLimits.map((c) => [c.categoryRef, c]));
    const fromTree: ChildLine[] = childNodes.map((node) => ({
      id: node.id,
      // Grandchildren disambiguate with their breadcrumb ("Restaurantes › Delivery").
      label: node.depth >= 3 ? categoryPathLabel(categoryTree, node.id) ?? node.label : node.label,
      iconName: node.iconName,
      limit: limitByRef.get(node.id) ?? null,
    }));
    // A limit on a ref outside the reconstructed subtree still needs its line.
    const extras: ChildLine[] = childLimits
      .filter((c) => !childNodes.some((node) => node.id === c.categoryRef))
      .map((c) => {
        const meta = getCategoryMeta(categoryIndex, c.categoryRef);
        return {
          id: c.categoryRef,
          label: meta.pathLabel ?? (c.name || meta.label),
          iconName: c.icon ?? meta.iconName,
          limit: c,
        };
      });
    const merged = [...fromTree, ...extras].sort((a, b) =>
      a.label.localeCompare(b.label, 'pt-BR', { sensitivity: 'base' }),
    );
    return filterNeedle
      ? merged.filter((l) => normalizeForSearch(l.label).includes(filterNeedle))
      : merged;
  }, [childNodes, categoryTree, childLimits, categoryIndex, filterNeedle]);

  if (lines.length === 0) {
    return (
      <p className="px-4 pb-3 pl-13 text-2xs text-text-tertiary @2xl:pl-17">
        Sem subcategorias.
      </p>
    );
  }

  return (
    <ul className="space-y-0.5 px-4 pb-3 pl-13 @2xl:pl-17" role="list">
      {lines.map((line) => (
        <ChildRow
          key={line.id}
          row={row}
          line={line}
          hasRules={ruleRefs.has(line.id)}
          editing={!readOnly && editingRef === line.id}
          saving={savingRef === line.id}
          limitsBusy={limitsBusy}
          readOnly={readOnly}
          limitHint={childLimitHint}
          onStartEditing={setEditingRef}
          onSaveLimit={onSaveLimit}
          onViewChildTransactions={onViewChildTransactions}
          onViewChildRules={onViewChildRules}
        />
      ))}
    </ul>
  );
}

function ChildRow({
  row,
  line,
  hasRules,
  editing,
  saving,
  limitsBusy,
  readOnly,
  limitHint,
  onStartEditing,
  onSaveLimit,
  onViewChildTransactions,
  onViewChildRules,
}: {
  row: CategoryOverviewRow;
  line: ChildLine;
  hasRules: boolean;
  editing: boolean;
  saving: boolean;
  limitsBusy: boolean;
  readOnly: boolean;
  limitHint: string;
  onStartEditing: (ref: string | null) => void;
  onSaveLimit: CategoryRowsProps['onSaveLimit'];
  onViewChildTransactions: (rootRef: string, leafId: string) => void;
  onViewChildRules: (rootRef: string) => void;
}) {
  const formatCurrency = useFormatCurrency();
  const { limit } = line;
  const status = limit ? STATUS_META[limit.status] : null;
  // Children inherit the parent hue at a shifted lightness — the same resolver every
  // category surface uses.
  const visual = categoryVisual(line.id, 'pluggy');

  const startEditing = () => onStartEditing(line.id);
  const commitLimit = (value: number | null) => {
    onSaveLimit(line.id, value);
    onStartEditing(null);
  };
  const cancelEditing = () => onStartEditing(null);
  const viewTransactions = () => onViewChildTransactions(row.categoryRef, line.id);
  const viewRules = () => onViewChildRules(row.categoryRef);

  return (
    <li className="rounded-lg px-2 py-1.5">
      <div className="flex items-center gap-2.5">
        <CategoryTile size="sm" icon={line.iconName} visual={visual} />
        <span className="min-w-0 flex-1 truncate text-sm text-text-primary">{line.label}</span>
        {limit && status && !editing && (
          <span className="flex items-center gap-2">
            <BurnBar percent={limit.burnPercent ?? 0} statusKey={limit.status} className="hidden w-28 sm:flex" />
            {readOnly ? (
              <span className="inline-flex items-center gap-1 font-mono text-2xs text-text-secondary tabular-nums">
                de <MoneyValue text={formatCurrency(limit.limit)} />
              </span>
            ) : (
              <button
                type="button"
                onClick={startEditing}
                disabled={limitsBusy}
                className="group/cl inline-flex cursor-pointer items-center gap-1 font-mono text-2xs text-text-secondary tabular-nums hover:text-text-primary disabled:cursor-not-allowed"
              >
                de <MoneyValue text={formatCurrency(limit.limit)} />
                <PencilSimpleIcon className="size-3 text-neutral-300 transition-colors group-hover/cl:text-text-tertiary" />
              </button>
            )}
          </span>
        )}
        {!limit && !editing && !readOnly && row.kind === 'expense' && !row.isArchived && (
          <Button variant="secondary" size="sm" onClick={startEditing} disabled={limitsBusy} className="h-7 px-2.5 text-2xs">
            <PlusIcon className="size-3" /> Definir limite
          </Button>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={`Ações de ${line.label}`}
              className="grid size-6 shrink-0 cursor-pointer place-items-center rounded-md text-text-tertiary transition-colors hover:bg-accent hover:text-text-primary"
            >
              <DotsThreeVerticalIcon className="size-4" weight="bold" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" sideOffset={4} className="w-56">
            {/* Children inherit their root's nature — no limits under income/neutral roots. */}
            {row.kind === 'expense' && !readOnly && (
              <DropdownMenuItem className="cursor-pointer gap-2" disabled={limitsBusy} onSelect={startEditing}>
                <WalletIcon className="size-4" />
                {limit ? 'Alterar limite' : 'Definir limite'}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem className="cursor-pointer gap-2" onSelect={viewTransactions}>
              <ListMagnifyingGlassIcon className="size-4" />
              Ver lançamentos
            </DropdownMenuItem>
            {hasRules && (
              <DropdownMenuItem className="cursor-pointer gap-2" onSelect={viewRules}>
                <FunnelIcon className="size-4" />
                Regras desta categoria
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {editing && (
        <span className="mt-1.5 flex items-center gap-1.5 pl-10.5">
          <InlineLimitEditor
            size="sm"
            initialValue={limit?.limit ?? null}
            saving={saving || limitsBusy}
            onCommit={commitLimit}
            onCancel={cancelEditing}
          />
          <span className="text-2xs text-text-tertiary">
            {limit ? '0 ou vazio remove o limite' : limitHint}
          </span>
        </span>
      )}
    </li>
  );
}
